import { EntryType } from 'common/enums/entry-type.enum';
import { FacilityCodeType } from 'common/enums/facility-code-type.enum';
import { GuestType } from 'common/enums/guest-type.enum';
import { GuestValidity } from 'common/enums/guest-validity.enum';
import { INullableValue } from 'common/models/nullable-value.interface';
import { DirectionEnum } from 'features/eiq-secure/enums/direction.enum';

export interface ISystemConfigurationUpdate {
  default: IDefaultConfigurationUpdate | null;
  serviceHours: IServiceHoursConfigurationUpdate | null;
  citations: ICitationConfigurationUpdate | null;
  guestLimitations: IGuestLimitationsConfiguration | null;
  cardAccessConfiguration: IUpdateCardAccessConfiguration | null;
}

export interface IAdvisoryConfigurationUpdate {
  advisory: IAdvisoryConfiguration | null;
}

export interface IMassAlertsConfigurationUpdate {
  massAlerts: IMassAlertsConfiguration | null;
}

export interface IDriveIdConfigurationUpdate {
  driveIdConfiguration: IDriveIdConfiguration | null;
}

export interface IPlateScanConfigurationUpdate {
  plateScan: IPlateScanConfiguration | null;
}

export interface ISmartPlateConfigurationUpdate {
  smartPlate: ISmartPlateConfiguration | null;
}

export interface IGageConfigurationUpdate {
  name: string;
  useRtsp: boolean;
  rtspUrl: string | null;
  useGateController: string | null;
}

export interface IDriveIdConfiguration {
  isCaptureDriverName: boolean | null;
}

export interface ISystemConfigurationRead {
  default: IDefaultConfiguration;
  serviceHours: IServiceHoursConfiguration;
  gates: IGateConfiguration[];
  rejectVehicleReasons: IRejectVehicleReason[];
  facilityCodes: IFacilityCodeResponse[];
  guestTypes: GuestType[];
  vehicleDeactivationReasons: string[];
  citations: ICitationConfiguration;
  incidentCategories: IIncidentCategory[];
  guestLimitations: IGuestLimitationsConfiguration;
  cardAccessConfiguration: ICardAccessConfiguration;
  plateScan: IPlateScanConfiguration | null;
  smartPlate: ISmartPlateConfiguration;
  driveIdConfiguration?: IDriveIdConfiguration;
  welcomeEmails?: IWelcomeEmailsConfiguration;
  trafficLogArchive?: ITrafficLogArchiveConfiguration;
  eiqVoiceConfiguration?: IEIQVoiceConfiguration;
  payEase?: IPayEaseConfiguration;
  rentalPortal?: IRentalPortalConfiguration;
  massAlerts: IMassAlertsConfiguration;
  advisory: IAdvisoryConfiguration;
}

export interface IAdvisoryConfiguration {
  isDisplayReasonForSecureOfficer: boolean;
}

export interface IMassAlertsConfiguration {
  emailTitle: string;
}

export interface IEIQVoiceConfiguration {
  version: string;
}

export interface ITrafficLogArchiveConfiguration {
  trafficLogDaysToArchive: number;
}

export interface IPayEaseConfiguration {
  pinCode: string | null;
  eiqCommissionInPercentages?: number;
  terminalReaderId?: string | null;
}

export interface IPayEaseConfigurationUpdate {
  payEase: {
    pinCode: string | null;
    eiqCommissionInPercentages: number | null;
    terminalReaderId: string | null;
  };
}

export interface IRentalPortalConfiguration {
  eiqCommissionInPercentages?: number;
  isPropertyFeeEnabled?: boolean;
  propertyRegistrationFee?: number;
  propertyRenewalFee?: number;
  isReservationFeeEnabled?: boolean;
  reservationStandardFee?: number;
  reservationExpeditedFee?: number;
  reservationExpressFee?: number;
  isAgencyChangeFeeEnabled?: boolean;
  agencyChangeFee?: number;
  minimumReservationNights?: number;
}

export interface IRentalPortalConfigurationUpdate {
  rentalPortal: {
    eiqCommissionInPercentages?: number | null;
    isPropertyFeeEnabled?: boolean;
    propertyRegistrationFee?: number;
    propertyRenewalFee?: number;
    isReservationFeeEnabled?: boolean;
    reservationStandardFee?: number;
    reservationExpeditedFee?: number;
    reservationExpressFee?: number;
    isAgencyChangeFeeEnabled?: boolean;
    agencyChangeFee?: number;
    minimumReservationNights?: number;
  };
}

export interface IUpdateEIQVoiceConfiguration {
  EIQVoiceConfiguration: IEIQVoiceConfiguration;
}

export interface ITrafficLogArchiveConfigurationUpdate {
  updateTrafficLogArchive: ITrafficLogArchiveConfiguration;
}

export interface IWelcomeEmailsConfiguration {
  lastSendToAll: string | null;
  lastSendToUnregistered: string | null;
}

export interface IUpdateCardAccessConfiguration {
  logImportIntervalInMinutes: number | null;
}

export interface ICardAccessConfiguration extends IUpdateCardAccessConfiguration {
  groupNumber: number;
  serverName: string;
  lastPushAt: string | null;
}

export interface IFacilityCodeResponse {
  value: string;
  cardAccessSystemValue: string;
  isSyncToCardAccessSystem: boolean;
  defaultForType: FacilityCodeType | null;
}

export interface IIncidentCategory {
  name: string;
  subCategories: string[];
}

export interface ICameraConfiguration {
  name: string;
  direction: DirectionEnum;
}

export interface IGateConfiguration {
  name: string;
  longitude: number | null;
  latitude: number | null;
  useRtsp: boolean;
  rtspUrl: string | null;
  useGateController: string | null;
  panelNumber: string | null;
  cameras?: ICameraConfiguration[];
  readers?: IReaderConfiguration[];
  relays?: IRelayConfiguration[];
}

export interface IRelayConfiguration {
  number: string | null;
  name: string | null;
}

export interface IReaderConfiguration {
  number: string;
  direction: DirectionEnum;
  entryType: EntryType | null;
  name: string | null;
}

export interface IDefaultConfigurationUpdateRequest {
  default: IDefaultConfigurationUpdate;
}

export interface IDefaultConfigurationUpdate {
  speedLimit?: INullableValue<number> | null;
  guestValidity?: GuestValidity | null;
  guestType?: GuestType | null;
  isShowPin?: boolean;
  defaultLicenseState?: string | null;
  expiredGuestRemoveDelayInDays?: number | null;
  vehicleDeletionGracePeriodDays?: number | null;
  communityRules?: string | null;
  isShowPinConnect?: boolean;
  isShareGuestConnect?: boolean;
  isShareVehicleConnect?: boolean;
  isAlertMessageEditableForSecure?: boolean;
  isGuestListEditableInSecure?: boolean;
  isGuestRestrictionsEditableInSecure?: boolean;
  speedLimitImageUrl?: string | null;
  isDisplayResidentOwnTrafficInConnect?: boolean;
  isLimitTrafficHistoryInConnect?: boolean;
  isDisplaySharedGuestTrafficInConnect?: boolean;
  communityLogoUrl?: string | null;
  entryType?: string | null;
  eiqPassportInstractionText?: string | null;
  eiqPassportShareMessage?: string | null;
}

export interface IDefaultConfiguration {
  communityName: string | null;
  guestValidity: GuestValidity | null;
  guestType: GuestType | null;
  isShowPin: boolean;
  defaultLicenseState: string | null;
  expiredGuestRemoveDelayInDays: number | null;
  vehicleDeletionGracePeriodDays: number | null;
  communityRules: string | null;
  isShowPinConnect: boolean;
  isShareGuestConnect: boolean;
  isShareVehicleConnect: boolean;
  isAlertMessageEditableForSecure: boolean;
  isGuestListEditableInSecure: boolean;
  isGuestRestrictionsEditableInSecure: boolean;
  speedLimitImageUrl: string | null;
  speedLimit: number | null;
  isDisplayResidentOwnTrafficInConnect: boolean;
  isLimitTrafficHistoryInConnect: boolean;
  isDisplaySharedGuestTrafficInConnect: boolean;
  communityLogoUrl: string | null;
  entryType: string | null;
}

export interface IServiceHoursConfiguration {
  mondayToFridayStartTime: string | null;
  mondayToFridayEndTime: string | null;
  saturdayStartTime: string | null;
  saturdayEndTime: string | null;
  sundayStartTime: string | null;
  sundayEndTime: string | null;
  [key: string]: string | null | undefined;
}

export interface IServiceHoursConfigurationUpdate {
  mondayToFridayPeriod: ITimeOnlyPeriod | null;
  saturdayPeriod: ITimeOnlyPeriod | null;
  sundayPeriod: ITimeOnlyPeriod | null;
}

interface ITimeOnlyPeriod {
  startTime: string | null;
  endTime: string | null;
}

export interface IRejectVehicleReason {
  reason: string;
  description: string;
}

export interface ICitationConfiguration {
  citationPaymentCommissionInPercentages: number;
  canceledCitationRemoveDelayInDays: number;
  generalInformation: string | null;
  unassignedCitationRemoveDelayInDays: number;
  citationResetInDays: number;
  delinquentCitationInDays: number;
  isAutoSuspendTransponders: boolean;
  isAutoIssueInvoice: boolean;
  isPreventVendorCitations: boolean;
  firstCitationWarningToleranceInMPH: number | null;
  firstCitationEscalationToleranseInMPH: number | null;
  rules: ICitationRuleConfiguration[];
}

export interface ICitationConfigurationUpdate {
  citations: {
    citationPaymentCommissionInPercentages: number | null;
    canceledCitationRemoveDelayInDays: number | null;
    generalInformation: string | null;
    unassignedCitationRemoveDelayInDays: number | null;
    citationResetInDays: number | null;
    delinquentCitationInDays: number | null;
    isAutoSuspendTransponders: boolean | null;
    isAutoIssueInvoice: boolean | null;
    isPreventVendorCitations: boolean | null;
    firstCitationWarningToleranceInMPH: INullableValue<number> | null;
    firstCitationEscalationToleranseInMPH: INullableValue<number> | null;
    rules: ICitationRuleConfiguration[] | null;
  };
}

export interface ICitationRuleConfiguration {
  order: number;
  legalNotes?: string;
  escalationTiers: ICitaionRuleEscalationTierConfiguration[];
}

export interface ICitaionRuleEscalationTierConfiguration {
  order: number;
  speedFrom: number | null;
  speedTo: number | null;
  isWarningOnly: boolean;
  residentFineAmount?: number | null;
  residentSuspendTransponderInDays?: number | null;
  visitorFineAmount?: number | null;
  visitorSuspendTransponderInDays?: number | null;
  revokeDrivingPrivilegesInDays?: number | null;
}

export interface IGuestLimitationsConfiguration {
  permanentGuestsLimitation: number;
  guestVisitDaysLimitation: number;
  maximumGuestValidityLimitation: number;
  maximumStartDateAheadLimitation: number;
  ownershipTypesConfiguration: OwnershipTypeConfiguration[];
}

export interface OwnershipTypeConfiguration {
  type: string;
  isLimited: boolean;
}

export interface IPlateScanConfiguration {
  displayFailedNotification: boolean;
}

export enum SmartPlateAlgorithmType {
  W34N = 'w34N',
  W26S = 'w26S',
  W37S = 'w37S',
}

export interface ISmartPlateConfiguration {
  algorithmType: SmartPlateAlgorithmType;
  facilityCode: number | null;
  lastRegeneration?: string | null;
}

export interface ICommunitySettings {
  city: string;
  state: string;
  identifier: string;
  eiqVoicePhoneNumber: string | null;
  verificationBackupCode: string | null;
  cardAccessSettings: ICardAccessSettings;
}

export interface ICardAccessSettings {
  serverName: string;
  cardAccessGroupsSettings: string[];
}

export interface IDriveIdSettings {
  licenseKey: string | null;
  sdkLocation: string | null;
}
